/**
 * Normalize admin (flat customer) vs agent (chat + customerDetail) rows
 * so the shared ChatPage can read a consistent shape.
 */

export function isAgentRole(user) {
  return user && user.role === 'admin';
}

export function isSuperAdminRole(user) {
  return user && user.role === 'superadmin';
}

export function getPageSize(user) {
  return isAgentRole(user) ? 50 : 10;
}

/**
 * Normalize a list row for display and selection.
 * Keeps `raw` so emit/end-chat can use the original API payload.
 */
export function normalizeChatUser(user, isAgent) {
  if (!user) {
    return null;
  }

  if (isAgent) {
    const detail = user.customerDetail && user.customerDetail[0]
      ? user.customerDetail[0]
      : null;
    if (!detail) {
      return null;
    }
    return {
      id: detail._id || user.customerId || user._id,
      chatId: user._id,
      customerId: user.customerId,
      name: detail.name,
      mobileNumber: detail.mobileNumber,
      context: detail.context,
      entity: detail.entity || [],
      status: user.status,
      color: detail.color || user.color,
      source: detail.source,
      language: detail.langugae || detail.language,
      updatedAt: user.updatedAt,
      highlited: user.highlited,
      raw: user,
      detail,
    };
  }

  return {
    id: user._id || user.mobileNumber,
    chatId: user._id,
    customerId: user._id,
    name: user.name,
    mobileNumber: user.mobileNumber,
    context: user.context,
    entity: user.entity || [],
    status: user.status,
    color: user.color,
    source: user.source,
    language: user.langugae || user.language,
    updatedAt: user.updatedAt,
    highlited: user.highlited,
    raw: user,
    detail: user,
  };
}

export function normalizeChatUsers(users, isAgent) {
  if (!users || !users.length) {
    return [];
  }
  return users
    .map((user) => normalizeChatUser(user, isAgent))
    .filter(Boolean);
}

/** Display mobile: prefer numeric mobileNumber, else entity mobile_number. */
export function getDisplayMobile(user) {
  if (!user) {
    return 'Not Applicable';
  }
  if (!isNaN(parseInt(user.mobileNumber, 10))) {
    return user.mobileNumber;
  }
  const entityMobile =
    user.entity &&
    user.entity.length > 0 &&
    user.entity.find((obj) => obj.entity === 'mobile_number');
  if (entityMobile && !isNaN(parseInt(entityMobile.value, 10))) {
    return entityMobile.value;
  }
  return 'Not Applicable';
}

export function formatLanguage(language) {
  if (!language) {
    return '';
  }
  return language.charAt(0).toUpperCase() + language.slice(1);
}

/**
 * API returns messages newest-first. Reverse that for the thread:
 * oldest at the top, latest at the bottom (WhatsApp-style).
 * Sort by _id when timestamps collide so pagination pages don't interleave.
 */
export function toChronologicalMessages(messages) {
  if (!messages || !messages.length) {
    return [];
  }
  return messages.slice().sort((a, b) => {
    const aTime = new Date(a && a.createdAt ? a.createdAt : 0).getTime();
    const bTime = new Date(b && b.createdAt ? b.createdAt : 0).getTime();
    if (aTime !== bTime) {
      return aTime - bTime;
    }
    return String(a && a._id ? a._id : '').localeCompare(String(b && b._id ? b._id : ''));
  });
}
